// Core data model for i-seng. Everything here describes data that lives only
// in memory, in the user's browser tab. Nothing is persisted or transmitted.

export type GenomeFormat = '23andme' | 'ancestry' | 'vcf' | 'unknown';

export interface Variant {
  /** dbSNP rsID (e.g. "rs4477212") or "." when unknown. */
  rsid: string;
  /** Normalized chromosome label: "1".."22", "X", "Y", "MT". */
  chrom: string;
  /** 1-based genomic position. */
  pos: number;
  /**
   * Genotype as a short string.
   * - Array-format (23andMe/Ancestry): the two called alleles, e.g. "AG", "TT", "--".
   * - VCF: the called alleles resolved from GT against REF/ALT when a sample
   *   column is present; otherwise the REF>ALT change, e.g. "C>T".
   */
  genotype: string;
  /** Reference allele (VCF only). */
  ref?: string;
  /** Alternate allele(s), comma-joined (VCF only). */
  alt?: string;
}

export interface ParseWarning {
  line: number;
  message: string;
}

export interface ParsedGenome {
  format: GenomeFormat;
  variants: Variant[];
  meta: {
    fileName: string;
    /** Total non-empty lines seen in the source. */
    lineCount: number;
    /** True if parsing stopped early at the variant cap. */
    truncated: boolean;
    /** Epoch ms when parsing finished (set by the UI, not the parser). */
    parsedAt: number;
    /**
     * Reference assembly, when the file actually says. Only VCFs carry this —
     * array exports have no header field for it, so they stay null and the UI
     * falls back to GRCh37, which is what 23andMe and AncestryDNA emit.
     */
    assembly?: 'hg19' | 'hg38' | null;
    /** How `assembly` was determined, for display. */
    assemblySource?: 'reference-header' | 'contig-length' | null;
  };
  warnings: ParseWarning[];
}

/** The chromosomes we recognize, in display order. */
export const CHROM_ORDER: string[] = [
  '1', '2', '3', '4', '5', '6', '7', '8', '9', '10', '11', '12',
  '13', '14', '15', '16', '17', '18', '19', '20', '21', '22', 'X', 'Y', 'MT',
];

/** Approximate GRCh38 chromosome lengths (bp), for ideogram scaling. */
export const CHROM_LENGTHS: Record<string, number> = {
  '1': 248956422, '2': 242193529, '3': 198295559, '4': 190214555,
  '5': 181538259, '6': 170805979, '7': 159345973, '8': 145138636,
  '9': 138394717, '10': 133797422, '11': 135086622, '12': 133275309,
  '13': 114364328, '14': 107043718, '15': 101991189, '16': 90338345,
  '17': 83257441, '18': 80373285, '19': 58617616, '20': 64444167,
  '21': 46709983, '22': 50818468, 'X': 156040895, 'Y': 57227415, 'MT': 16569,
};
